import React, { ReactElement } from "react";
import { RouterProps, Router } from "@swiggy-private/core-ui";
import { CartRoutes, CART_OUT_IN_ROUTES } from "@swiggy-private/cart-client";
import { RouteContainer, CheckoutRoute, isQuickCheckoutFlow } from "@swiggy-private/checkout-core";
import { PaymentRoutes, OUT_IN_ROUTES } from "@swiggy-private/payment-client";

export type CheckoutRouterProps = Omit<RouterProps<CheckoutRoute>, "children">;

// Union of payment + cart routes that must use non-overlapping (out-in) ScreenTransition:
// exit finishes before enter starts. Needed so QC ↔ payments / confirm hops do not stack
// two full-screen layers while webview height / transparent-bg callbacks run.
const NON_OVERLAPPING_ROUTES = [...OUT_IN_ROUTES, ...CART_OUT_IN_ROUTES];

export const CheckoutRouter = (props: CheckoutRouterProps): ReactElement => {
    const routes = [...PaymentRoutes];
    routes.push(...CartRoutes);
    // QC/AIP only: CartMain nests QC ↔ MRO (and other cart siblings) under one outer key
    // (`CheckoutRoute.CART`). Height / bg callbacks are registered via
    // `navigateTo(..., { onTransitionComplete })` and flushed by the *nested*
    // CartMain ScreenTransition. Without this flag, outer MiniRouter would clear
    // that pending callback on same-outer-key sibling hops (and on RouteContainer
    // sync navigations that omit the options object). Leave off for BAU.
    const preservePendingTransitionOnSiblingNav = isQuickCheckoutFlow();
    const ignoreBubbledTransitionEnd = isQuickCheckoutFlow();
    return (
        <Router
            {...props}
            container={RouteContainer}
            nonOverlappingTransitionRoutes={NON_OVERLAPPING_ROUTES}
            preservePendingTransitionOnSiblingNav={preservePendingTransitionOnSiblingNav}
            ignoreBubbledTransitionEnd={ignoreBubbledTransitionEnd}
        >
            {routes}
        </Router>
    );
};
